import { useMemo } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, RotateCcw, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './UIComponents';
import { useUploadManager } from '../contexts/UploadManagerContext';

function formatStatusLabel(stageLabel: string) {
  return stageLabel || 'Working…';
}

export default function UploadsTray() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    jobs,
    trayHidden,
    trayOpen,
    setTrayOpen,
    setTrayHovering,
    noteTrayInteraction,
    closeTray,
    cancelJob,
    retryJob,
    dismissJob,
  } =
    useUploadManager();

  const hideOnThisRoute =
    location.pathname.startsWith('/transcription/') || location.pathname.startsWith('/trial/transcription/');

  // Only show on /upload page
  const showOnlyOnUpload = location.pathname !== '/upload';

  const counts = useMemo(() => {
    const active = jobs.filter((j) =>
      ['queued', 'preparing', 'hashing', 'uploading', 'creating_transcription', 'finalizing'].includes(j.status)
    ).length;
    const failed = jobs.filter((j) => j.status === 'error').length;
    const complete = jobs.filter((j) => j.status === 'complete').length;
    return { active, failed, complete };
  }, [jobs]);

  const overallProgress = useMemo(() => {
    const activeJobs = jobs.filter((j) =>
      ['queued', 'preparing', 'hashing', 'uploading', 'creating_transcription', 'finalizing'].includes(j.status)
    );
    if (activeJobs.length === 0) return 100;
    const sum = activeJobs.reduce((acc, j) => acc + j.progress, 0);
    return Math.round(sum / activeJobs.length);
  }, [jobs]);

  if (jobs.length === 0 || hideOnThisRoute || showOnlyOnUpload || trayHidden) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-3rem)]"
      onMouseEnter={() => setTrayHovering(true)}
      onMouseLeave={() => setTrayHovering(false)}
      onPointerDown={() => noteTrayInteraction()}
    >
      {!trayOpen ? (
        <div className="w-full rounded-xl border border-slate-200 dark:border-zinc-800 bg-white/95 dark:bg-zinc-900/95 backdrop-blur shadow-lg px-4 py-3 hover:shadow-xl transition-shadow">
          <div className="flex items-start justify-between gap-3">
            <button
              type="button"
              className="min-w-0 flex-1 text-left"
              onClick={() => setTrayOpen(true)}
              aria-label="Open uploads tray"
            >
              <p className="text-sm font-semibold text-slate-900 dark:text-zinc-100 truncate">
                {counts.active > 0
                  ? `Uploading ${counts.active} file${counts.active === 1 ? '' : 's'}`
                  : `Uploads`}
              </p>
              <p className="text-xs text-slate-500 dark:text-zinc-400">
                {counts.active > 0
                  ? `${overallProgress}% • ${counts.failed > 0 ? `${counts.failed} failed` : 'No errors'}`
                  : `${counts.complete} complete${counts.failed ? ` • ${counts.failed} failed` : ''}`}
              </p>
            </button>

            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-500 dark:text-zinc-400"
                onClick={() => setTrayOpen(true)}
                aria-label="Expand uploads tray"
                title="Expand"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-500 dark:text-zinc-400"
                onClick={() => closeTray()}
                aria-label="Close uploads tray"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {counts.active > 0 && (
            <div className="mt-3 h-2 w-full rounded-full bg-slate-100 dark:bg-zinc-800 overflow-hidden">
              <div
                className="h-2 bg-indigo-600 transition-all duration-300 ease-out"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 dark:border-zinc-800 bg-white/95 dark:bg-zinc-900/95 backdrop-blur shadow-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 dark:border-zinc-800 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 dark:text-zinc-100">Uploads</p>
              <p className="text-xs text-slate-500 dark:text-zinc-400">
                {counts.active > 0
                  ? `${counts.active} active • ${counts.failed > 0 ? `${counts.failed} failed` : 'No errors'}`
                  : `${counts.complete} complete${counts.failed ? ` • ${counts.failed} failed` : ''}`}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-500 dark:text-zinc-400"
                onClick={() => setTrayOpen(false)}
                aria-label="Collapse uploads tray"
                title="Collapse"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-500 dark:text-zinc-400"
                onClick={() => closeTray()}
                aria-label="Close uploads tray"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100 dark:divide-zinc-800">
            {jobs.map((job) => {
              const isActive = ['queued', 'preparing', 'hashing', 'uploading', 'creating_transcription', 'finalizing'].includes(
                job.status
              );
              const canDismiss = ['complete', 'error', 'canceled'].includes(job.status);
              const canRetry = job.status === 'error' || job.status === 'canceled';
              return (
                <div key={job.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-zinc-100 truncate" title={job.displayName}>
                        {job.displayName}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-zinc-400">
                        {job.status === 'error' ? job.error : formatStatusLabel(job.stageLabel)}
                      </p>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {job.transcriptionId && job.status === 'complete' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => navigate(`/transcription/${job.transcriptionId}`)}
                        >
                          <span className="flex items-center gap-1">
                            Open <ExternalLink className="h-3.5 w-3.5" />
                          </span>
                        </Button>
                      )}
                      {isActive && (
                        <Button size="sm" variant="ghost" onClick={() => cancelJob(job.id)}>
                          Cancel
                        </Button>
                      )}
                      {canRetry && (
                        <Button size="sm" variant="ghost" onClick={() => retryJob(job.id)} title="Retry">
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                      {canDismiss && (
                        <Button size="sm" variant="ghost" onClick={() => dismissJob(job.id)} title="Dismiss">
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="mt-2">
                    <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-zinc-800 overflow-hidden">
                      <div
                        className={`h-2 transition-all duration-300 ease-out ${
                          job.status === 'error' ? 'bg-red-500' : job.status === 'complete' ? 'bg-emerald-500' : 'bg-indigo-600'
                        }`}
                        style={{ width: `${Math.max(0, Math.min(100, job.progress))}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
